import React from 'react';
import { Lock, Check, Sparkles } from 'lucide-react';

/**
 * Users Step - User license configuration
 *
 * - Mobile App: Checkbox to include/exclude, count locked to total platform users
 * - Excel Connect: Auto-calculated = total platform users (locked)
 */
const UsersStep = ({ quoteData, setQuoteData, pricing }) => {
  // SQL Connect and GL Connect users moved to Products page
  const userTypes = [
    { id: 'admin', name: 'Developer/Admin', price: pricing?.users?.admin?.price || 1000, desc: 'Full access to build and manage', category: 'Platform Users' },
    { id: 'readOnly', name: 'Read Only', price: pricing?.users?.readOnly?.price || 100, desc: 'View reports and dashboards', category: 'Platform Users' }
  ];

  // Calculate totals
  const adminCount = quoteData.users.admin || 0;
  const readOnlyCount = quoteData.users.readOnly || 0;
  const totalPlatformUsers = adminCount + readOnlyCount;

  // Mobile App enabled state (mobile > 0 means enabled)
  const isMobileEnabled = (quoteData.users.mobile || 0) > 0;

  // Update Excel Connect when platform users change
  React.useEffect(() => {
    if (quoteData.users.excel !== totalPlatformUsers) {
      setQuoteData({
        ...quoteData,
        users: { ...quoteData.users, excel: totalPlatformUsers }
      });
    }
  }, [totalPlatformUsers]);

  // Update Mobile when platform users change (only if mobile is enabled)
  React.useEffect(() => {
    if (isMobileEnabled && quoteData.users.mobile !== totalPlatformUsers) {
      setQuoteData({
        ...quoteData,
        users: { ...quoteData.users, mobile: totalPlatformUsers }
      });
    }
  }, [totalPlatformUsers, isMobileEnabled]);

  // Toggle Mobile App
  const toggleMobileApp = () => {
    setQuoteData({
      ...quoteData,
      users: {
        ...quoteData.users,
        mobile: isMobileEnabled ? 0 : totalPlatformUsers
      }
    });
  };

  // Input handler for editable fields
  const handleUserCountChange = (userId, value) => {
    const numValue = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
    setQuoteData({
      ...quoteData,
      users: { ...quoteData.users, [userId]: numValue }
    });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">User Licenses</h2>
      <p className="text-slate-600 mb-8">How many users will need access?</p>

      <div className="space-y-8">
        {['Platform Users'].map(category => (
          <div key={category}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-700">{category}</h3>
              {category === 'Platform Users' && totalPlatformUsers > 0 && (
                <span className="text-sm bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                  Total Platform Users: {totalPlatformUsers}
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {userTypes.filter(t => t.category === category).map(type => (
                <div
                  key={type.id}
                  className="p-6 rounded-xl border bg-white border-slate-200 shadow-sm"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h4 className="font-semibold text-slate-800">{type.name}</h4>
                      <p className="text-sm text-slate-600 mt-1">{type.desc}</p>
                      <p className="text-xs text-slate-400 mt-1">SKU: {pricing.users[type.id]?.sku}</p>
                    </div>
                    <div className="text-right">
                      <div className={`text-lg font-bold ${type.price > 0 ? 'text-blue-600' : 'text-green-600'}`}>
                        {type.price > 0 ? `$${type.price.toLocaleString()}` : 'Included'}
                      </div>
                      {type.price > 0 && (
                        <div className="text-xs text-slate-500">/user/year</div>
                      )}
                    </div>
                  </div>

                  <input
                    type="number"
                    min="0"
                    value={quoteData.users[type.id] > 0 ? quoteData.users[type.id] : ''}
                    onChange={(e) => handleUserCountChange(type.id, e.target.value)}
                    onBlur={(e) => {
                      if (e.target.value === '') {
                        handleUserCountChange(type.id, '0');
                      }
                    }}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0"
                  />

                  {quoteData.users[type.id] > 0 && type.price > 0 && (
                    <div className="mt-2 text-right text-sm text-slate-600">
                      Subtotal: <span className="font-semibold">${(quoteData.users[type.id] * type.price).toLocaleString()}</span>
                    </div>
                  )}
                </div>
              ))}

              {/* Mobile App - Special card with checkbox */}
              {category === 'Platform Users' && (
                <div
                  className={`p-6 rounded-xl border-2 transition-all ${
                    isMobileEnabled
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      {/* Checkbox */}
                      <button
                        onClick={toggleMobileApp}
                        disabled={totalPlatformUsers === 0}
                        className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                          isMobileEnabled
                            ? 'bg-blue-600 border-blue-600'
                            : 'bg-white border-slate-300 hover:border-blue-400'
                        } ${totalPlatformUsers === 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        {isMobileEnabled && <Check size={14} className="text-white" />}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-slate-800">Mobile App</h4>
                          <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                            Add-on
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1">Mobile access for platform users</p>
                        <p className="text-xs text-slate-400 mt-1">SKU: {pricing.users.mobile?.sku}</p>
                      </div>
                    </div>
                  </div>

                  {/* Locked input showing count */}
                  <div className="relative">
                    <input
                      type="number"
                      value={isMobileEnabled ? totalPlatformUsers : 0}
                      readOnly
                      className={`w-full p-3 pr-10 border rounded-lg cursor-not-allowed ${
                        isMobileEnabled
                          ? 'border-blue-300 bg-blue-100 text-blue-700 font-semibold'
                          : 'border-slate-200 bg-slate-100 text-slate-400'
                      }`}
                    />
                    <Lock size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 ${isMobileEnabled ? 'text-blue-500' : 'text-slate-400'}`} />
                  </div>

                  {totalPlatformUsers === 0 && (
                    <p className="text-xs text-slate-500 mt-2">
                      Add platform users first to enable Mobile App
                    </p>
                  )}
                </div>
              )}

              {/* Excel Connect - Auto-calculated, always included */}
              {category === 'Platform Users' && (
                <div
                  className={`p-6 rounded-xl border-2 ${
                    totalPlatformUsers > 0
                      ? 'bg-blue-50 border-blue-400'
                      : 'bg-slate-50 border-slate-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-800">Excel Connect</h4>
                        <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                          Add-on
                        </span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">Auto-included for all platform users</p>
                      <p className="text-xs text-slate-400 mt-1">SKU: {pricing.users.excel?.sku}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-green-600">Included</div>
                    </div>
                  </div>

                  <div className="relative">
                    <input
                      type="number"
                      value={totalPlatformUsers}
                      readOnly
                      className={`w-full p-3 pr-10 border rounded-lg cursor-not-allowed ${
                        totalPlatformUsers > 0
                          ? 'border-blue-300 bg-blue-100 text-blue-700 font-semibold'
                          : 'border-slate-200 bg-slate-100 text-slate-400'
                      }`}
                    />
                    <Lock size={16} className={`absolute right-3 top-1/2 -translate-y-1/2 ${totalPlatformUsers > 0 ? 'text-blue-500' : 'text-slate-400'}`} />
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* AI Users */}
        <div>
          <h3 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Sparkles size={18} className="text-purple-600" />
            AI Users
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl border bg-white border-slate-200 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-semibold text-slate-800">AI Users</h4>
                    <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                      SplashAI
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">AI-powered analytics and natural language queries</p>
                  <p className="text-xs text-slate-400 mt-1">SKU: {pricing.users.aiUsers?.sku}</p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-purple-600">${pricing?.users?.aiUsers?.monthly || 29}</div>
                  <div className="text-xs text-slate-500">/user/month</div>
                  <div className="text-xs text-slate-400">(${pricing?.users?.aiUsers?.price || 348}/year)</div>
                </div>
              </div>

              <input
                type="number"
                min="0"
                value={quoteData.users.aiUsers > 0 ? quoteData.users.aiUsers : ''}
                onChange={(e) => handleUserCountChange('aiUsers', e.target.value)}
                onBlur={(e) => {
                  if (e.target.value === '') {
                    handleUserCountChange('aiUsers', '0');
                  }
                }}
                className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="0"
              />

              {quoteData.users.aiUsers > 0 && (
                <div className="mt-2 text-right text-sm text-slate-600">
                  Subtotal: <span className="font-semibold">${(quoteData.users.aiUsers * (pricing?.users?.aiUsers?.price || 348)).toLocaleString()}/yr</span>
                  <span className="text-xs text-slate-400 ml-1">(${(quoteData.users.aiUsers * (pricing?.users?.aiUsers?.monthly || 29)).toLocaleString()}/mo)</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        {(totalPlatformUsers > 0 || (quoteData.users.aiUsers || 0) > 0) && (
          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">License Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
              <div>
                <span className="text-blue-600">Admin/Dev:</span>
                <span className="ml-2 font-semibold">{adminCount}</span>
              </div>
              <div>
                <span className="text-blue-600">Read Only:</span>
                <span className="ml-2 font-semibold">{readOnlyCount}</span>
              </div>
              <div>
                <span className="text-purple-600">AI Users:</span>
                <span className="ml-2 font-semibold">{quoteData.users.aiUsers || 0}</span>
              </div>
              <div>
                <span className="text-blue-600">Mobile:</span>
                <span className="ml-2 font-semibold">{isMobileEnabled ? totalPlatformUsers : 'Not included'}</span>
              </div>
              <div>
                <span className="text-blue-600">Excel:</span>
                <span className="ml-2 font-semibold">{totalPlatformUsers}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UsersStep;
